import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api, fileUrl } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Play, Pause, SkipForward, X, Share2 } from "lucide-react";
import { toast } from "sonner";
import { useSeo } from "@/lib/seo";

export default function EventPlayer({ publicMode = false }) {
  const { id, shareToken } = useParams();
  useSeo({
    title: "Watch the film",
    description: "A cinematic reel of voices from the people who mattered — Chapter turns collected video messages into a single film.",
    path: publicMode ? `/c/${shareToken}/play` : `/events/${id}/play`,
    noindex: true,
  });
  const navigate = useNavigate();
  const [event, setEvent] = useState(null);
  const [subs, setSubs] = useState([]);
  const [idx, setIdx] = useState(-1); // -1 = intro
  const [playing, setPlaying] = useState(true);
  const videoRef = useRef(null);

  useEffect(() => {
    const load = async () => {
      try {
        if (publicMode) {
          const e = await api.get(`/public/events/${shareToken}`);
          setEvent(e.data);
          const s = await api.get(`/public/events/${shareToken}/submissions`);
          setSubs(s.data);
        } else {
          const e = await api.get(`/events/${id}`);
          setEvent(e.data);
          const s = await api.get(`/events/${id}/submissions`);
          setSubs(s.data);
        }
      } catch { toast.error("Could not load event"); }
    };
    load();
  }, [id, shareToken, publicMode]);

  const ordered = React.useMemo(() => {
    if (!event) return subs;
    const order = event.clip_order && event.clip_order.length ? event.clip_order : subs.map((s) => s.id);
    const map = Object.fromEntries(subs.map((s) => [s.id, s]));
    return order.map((sid) => map[sid]).filter(Boolean);
  }, [subs, event]);

  // Intro auto-advance
  useEffect(() => {
    if (idx === -1 && playing && event) {
      const t = setTimeout(() => setIdx(0), 3500);
      return () => clearTimeout(t);
    }
  }, [idx, playing, event]);

  // Auto-advance for photos (no onEnded event)
  useEffect(() => {
    if (current && current.media_type === "photo" && playing) {
      const t = setTimeout(() => onEnded(), 5000);
      return () => clearTimeout(t);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [current?.id, playing]);

  // Outro: idx === ordered.length

  const totalSteps = ordered.length + 2; // intro + clips + outro
  const stepDisplay = idx === -1 ? 1 : (idx >= ordered.length ? totalSteps : idx + 2);

  const onEnded = () => {
    if (idx < ordered.length - 1) setIdx(idx + 1);
    else setIdx(ordered.length); // go to outro
  };

  const togglePlay = () => {
    if (videoRef.current) {
      if (playing) videoRef.current.pause(); else videoRef.current.play();
    }
    setPlaying((p) => !p);
  };

  const skip = () => {
    if (idx === -1) setIdx(0);
    else if (idx < ordered.length - 1) setIdx(idx + 1);
    else setIdx(ordered.length);
  };

  const exit = () => {
    if (publicMode) navigate(`/c/${shareToken}`);
    else navigate(`/events/${id}`);
  };

  const shareReel = async () => {
    const url = event && event.share_token ? `${window.location.origin}/c/${event.share_token}/play` : window.location.href;
    await navigator.clipboard.writeText(url);
    toast.success("Reel link copied");
  };

  if (!event) return <div className="min-h-screen bg-black text-white grid place-items-center">Loading…</div>;
  if (ordered.length === 0) return (
    <div className="min-h-screen bg-black text-white grid place-items-center text-center p-8">
      <div>
        <p className="font-mono-label text-[var(--em-clay)]">No clips yet</p>
        <h2 className="font-serif-em text-3xl mt-3">The film begins with a voice.</h2>
        <Button onClick={exit} variant="outline" className="mt-6 text-white border-white/30 hover:bg-white/10 rounded-full">Back</Button>
      </div>
    </div>
  );

  const current = idx >= 0 && idx < ordered.length ? ordered[idx] : null;

  const isIntro = idx === -1;
  const isOutro = idx >= ordered.length;
  const isPhoto = current && current.media_type === "photo";

  return (
    <div className="min-h-screen bg-black text-white relative">
      {/* Film strip top */}
      <div className="film-strip h-8" />

      <div className="absolute top-12 left-0 right-0 z-30 px-6 md:px-10 flex items-center justify-between text-white/70">
        <div className="flex items-center gap-2">
          <span className="font-mono-label text-white/70">Reel · {stepDisplay} / {totalSteps}</span>
        </div>
        <div className="flex gap-2">
          <Button size="sm" variant="ghost" onClick={shareReel} data-testid="player-share-btn" className="text-white hover:bg-white/10 rounded-full"><Share2 size={16} className="mr-2" />Share</Button>
          <Button size="sm" variant="ghost" onClick={exit} data-testid="player-exit-btn" className="text-white hover:bg-white/10 rounded-full"><X size={16} /></Button>
        </div>
      </div>

      <div className="min-h-screen flex items-center justify-center pt-16 pb-20 px-6">
        {isIntro && (
          <div className="text-center max-w-3xl" data-testid="player-intro">
            <p className="font-mono-label text-[var(--em-clay)]">Chapter presents</p>
            <h1 className="font-serif-em text-5xl md:text-7xl mt-6 leading-tight">{event.intro_title || event.title}</h1>
            <p className="mt-6 text-white/60">{ordered.length} voices · {event.theme}</p>
          </div>
        )}
        {current && (
          <div className="w-full max-w-4xl" data-testid="player-clip">
            {isPhoto ? (
              <img src={fileUrl(current.id)} alt={current.contributor_name} className="w-full rounded-xl bg-black aspect-video object-contain" />
            ) : (
              <video
                ref={videoRef}
                src={fileUrl(current.id)}
                className="w-full rounded-xl bg-black aspect-video"
                autoPlay
                playsInline
                controls={false}
                onEnded={onEnded}
                onPlay={() => setPlaying(true)}
                onPause={() => setPlaying(false)}
              />
            )}
            <div className="mt-5 flex items-end justify-between gap-4">
              <div>
                <p className="font-mono-label text-[var(--em-clay)]">{current.theme_group ? current.theme_group.replaceAll("_"," ") : "A voice"}</p>
                <h3 className="font-serif-em text-3xl mt-2">{current.contributor_name}</h3>
                <p className="text-white/60 mt-1">{current.prompt}</p>
                {current.note && <p className="text-white/50 mt-2 italic">"{current.note}"</p>}
              </div>
            </div>
          </div>
        )}
        {isOutro && (
          <div className="text-center max-w-3xl" data-testid="player-outro">
            <p className="font-mono-label text-[var(--em-clay)]">— Fin</p>
            <h1 className="font-serif-em text-4xl md:text-6xl mt-6 leading-tight">{event.outro_title || "Thank you for being part of this story."}</h1>
            <Button onClick={exit} className="mt-8 rounded-full bg-[var(--em-clay)] hover:bg-[var(--em-clay-deep)] text-white btn-lift">Close reel</Button>
          </div>
        )}
      </div>

      {/* Controls */}
      <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-30 flex items-center gap-2 bg-white/10 backdrop-blur-xl border border-white/20 rounded-full px-3 py-2">
        <Button size="icon" variant="ghost" onClick={togglePlay} data-testid="player-playpause-btn" className="text-white hover:bg-white/10 rounded-full">
          {playing ? <Pause size={18} /> : <Play size={18} />}
        </Button>
        <Button size="icon" variant="ghost" onClick={skip} data-testid="player-skip-btn" className="text-white hover:bg-white/10 rounded-full"><SkipForward size={18} /></Button>
      </div>

      <div className="film-strip h-8 fixed bottom-0 left-0 right-0" />
    </div>
  );
}
